import type { TripPanelType } from '@elara/client-core/states/TripsState';

import type { IconName } from '#ui/index.js';

import React, { useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { TRIP_PANEL_ICONS } from '@elara/client-core/constants';
import { cn } from '@elara/client-core/lib/utils';

import { sentry } from '#lib/sentry.js';
import {
  useChatsState,
  useCurrentChat,
  useItineraryState,
  useMessageViewState,
  useProductTourState,
  useRouterState,
  useTripsState,
  useUIState,
} from '#states/hooks.js';
import { BadResponseFeedbackDialog } from '#ui/chat/BadResponseFeedbackDialog.js';
import { Alert, AlertDescription, Badge, Button, Loader, MaterialIcon } from '#ui/index.js';
import { ChatInputView } from '#views/chat/ChatInputView.jsx';
import { ChatMessagesView } from '#views/chat/ChatMessagesView.jsx';
import { TripPanelErrorFallback } from '#views/error/TripPanelErrorFallback.js';
import { AppLayout } from '#views/layout/AppLayout.jsx';

import { BookingsView } from './BookingsView.js';
import { EditTripDatesDialog } from './EditTripDatesDialog.js';
import { ItineraryView } from './ItineraryView.js';
import { MobileNavBar } from './MobileNavBar.js';
import { RenameTripDialog } from './RenameTripDialog.js';
import { ShareItineraryDialog } from './ShareItineraryDialog.js';
import { ShortlistView } from './ShortlistView.js';
import { SupportButton } from './SupportButton.js';
import { TripSettingsActions } from './TripSettingsActions.js';

import { ItineraryBuilderView } from '../../ui/itinerary/ItineraryBuilderView.js';
import { ProductTour } from '../tour/ProductTour.js';

/**
 * TripPage component displays the chat interface for a specific trip.
 * This shows the messages and input area when a specific trip is selected.
 */
export function TripPage() {
  const currentTrip = useTripsState().current;
  const currentChat = useCurrentChat();
  const chatsState = useChatsState();
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const routerState = useRouterState();
  const messageViewState = useMessageViewState();
  const productTourState = useProductTourState();

  // Reset message view UI state whenever the current trip changes
  useEffect(() => {
    messageViewState.reset();
  }, [currentTrip?.id, messageViewState]);

  // Show loading state while trip/chat data is being fetched
  const isLoadingTrips = !tripsState.recentTrips.isReady;
  const isLoadingChats = !chatsState.recentChats.isReady;
  const isCreatingNewTrip = tripsState.isCreatingNewTrip;

  const isChatVisible = tripsState.isPanelVisible('chat');
  const shouldShowChatPadding = !tripsState.isMobile;

  /** Show tools panel with ItineraryBuilder when chat is active and the trip has itinerary items */
  const showItineraryBuilder =
    tripsState.activePanel === 'chat' && itineraryState.items.length > 0 && !tripsState.isMobile;

  const isItineraryCollapsed = tripsState.isItineraryBuilderCollapsed;

  // Calculate Tailwind width classes based on current state
  const getPanelWidthClasses = () => {
    const shouldShowTools = tripsState.activePanel !== 'chat' || showItineraryBuilder;
    const isChatHidden = shouldShowTools && !showItineraryBuilder;

    if (tripsState.isMobile) return { tools: 'w-full' };

    return {
      tools: shouldShowTools
        ? showItineraryBuilder
          ? isItineraryCollapsed
            ? 'w-14'
            : 'w-1/2 lg:w-180 max-w-1/2'
          : 'w-full'
        : 'w-0',
      chat: isChatHidden ? 'opacity-0' : 'opacity-100',
    };
  };

  // No unsaved-changes route guard: an unsent draft is now persisted per-chat (ELARA-952) and
  // restored when the user returns, so blocking navigation to protect it is unnecessary friction.

  const getPanelIcon = (panelType: TripPanelType): IconName | undefined => {
    return TRIP_PANEL_ICONS[panelType];
  };

  const title = (
    <Button
      variant="header"
      className="font-semibold max-w-full truncate -ml-1"
      onClick={() => tripsState.openRenameDialog(currentTrip?.displayName || '')}
    >
      <span className="truncate">{currentTrip?.displayName || ''}</span>
    </Button>
  );

  const navTabs = tripsState.availablePanels.map((panelType) => ({
    value: panelType,
    label: panelType.charAt(0).toUpperCase() + panelType.slice(1),
    icon: getPanelIcon(panelType),
    tourHighlight: `nav-${panelType}`,
  }));

  return (
    <AppLayout
      title={currentTrip && title}
      // Keyed by the trip, not the full path: switching panels (/trip/:id/:panel) is not a page
      // transition, so it must not replay the page fade (ELARA-704).
      transitionKey={currentTrip ? `trip-${currentTrip.id}` : routerState.currentRoute?.path}
      headerContent={currentTrip && <TripNavigationTabs navTabs={navTabs} className={cn('z-1 hidden')} />}
      allowScroll={false}
    >
      <div className="flex flex-col h-full">
        {productTourState.isResettingDemoTrip ||
        productTourState.isCreatingDemoTrip ||
        productTourState.isDemoDataLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center overflow-scroll gap-4">
            <Loader loading={true} />
            <p className="text-muted-foreground">
              {productTourState.isResettingDemoTrip ? 'Resetting demo trip...' : 'Preparing your demo trip...'}
            </p>
          </div>
        ) : currentTrip && currentChat ? (
          <>
            {/* Read-only banner for privileged users viewing another user's trip */}
            {tripsState.isViewingOtherUserTrip && (
              <Alert
                className={cn(
                  'flex items-center gap-2 border-b rounded-none',
                  'mb-0 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
                  'relative z-10', // Ensure banner appears above other content
                )}
              >
                <MaterialIcon icon="visibility" size="xs" className="text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  <span className="font-medium">Read-only view</span> — You are viewing another user's trip for
                  debugging purposes.
                </AlertDescription>
              </Alert>
            )}
            <div className={cn('flex-1 w-full flex overflow-y-hidden relative')}>
              {/* Product tour overlay */}
              <ProductTour />

              {/* Chat panel */}
              <div
                className={cn(
                  'relative h-full flex flex-col overflow-hidden flex-1',
                  'transition-all duration-300 ease-in-out opacity-100',
                  !isChatVisible && 'hidden',
                  shouldShowChatPadding ? 'pb-4' : 'pb-3',
                  getPanelWidthClasses().chat,
                )}
              >
                {/* Trip header */}
                {!showItineraryBuilder && (
                  <div className={cn('hidden md:flex gap-1 items-center justify-center px-4 mt-4 mb-2 min-h-10')}>
                    <div
                      className="text-sm font-semibold hover:bg-background-tertiary rounded-sm py-1 px-2 cursor-pointer max-w-2xl overflow-hidden truncate"
                      onClick={() => tripsState.openRenameDialog(currentTrip.displayName)}
                    >
                      {currentTrip.title}
                    </div>

                    {currentTrip.isDemo && <Badge variant="info">Demo</Badge>}
                  </div>
                )}
                {/* Messages area - takes remaining space and scrolls independently */}
                <div className="relative flex-1 overflow-hidden">
                  <div className={cn('absolute top-0 md:top-0 left-0 right-0 z-30 h-12 progressive-header-blur')} />

                  <ChatMessagesView />
                </div>
                {/* Input area - fixed at bottom */}
                <ChatInputView key={currentChat.id} />
                {/* Central bad-response feedback dialog for model messages */}
                <BadResponseFeedbackDialog
                  open={messageViewState.feedbackDialogOpen}
                  onOpenChange={(open) => {
                    if (!open) messageViewState.closeFeedbackDialog();
                  }}
                  onSubmit={async (comment) => {
                    await messageViewState.submitFeedbackForDialog(comment);
                  }}
                />
              </div>

              {/* Tools panel */}
              <div
                className={cn(
                  'h-full flex gap-4 flex-shrink-0 z-40',
                  'transition-all duration-300 ease-in-out',
                  'pb-3 md:pb-0',
                  // On mobile, hide the container when chat is active and no recommendations to show
                  tripsState.isMobile && tripsState.activePanel === 'chat' && !showItineraryBuilder && 'hidden',
                  tripsState.isPreviewPanelOpen && 'opacity-50',
                  getPanelWidthClasses().tools,
                )}
              >
                <div
                  className={cn(
                    'relative h-full w-full overflow-x-hidden',
                    'transition-transform ease-in-out',
                    tripsState.activePanel !== 'chat' || showItineraryBuilder
                      ? 'opacity-100 delay-300 duration-300'
                      : 'opacity-0 pointer-events-none duration-150',
                    showItineraryBuilder && 'border-l',
                    (tripsState.activePanel === 'shortlist' || tripsState.activePanel === 'itinerary') &&
                      'bg-background',
                    tripsState.activePanel === 'bookings' && itineraryState.items.length === 0 && 'bg-background',
                  )}
                >
                  <div className="@container h-full w-full overflow-y-auto overflow-x-hidden">
                    {showItineraryBuilder && (
                      <ErrorBoundary
                        FallbackComponent={(props) => (
                          <TripPanelErrorFallback {...props} viewName="itinerary-builder" />
                        )}
                        onError={(error, errorInfo) => {
                          console.error('Itinerary builder view error:', error, errorInfo);
                          sentry?.captureException(error);
                        }}
                        onReset={() => {
                          // Reset is handled by the error boundary's resetErrorBoundary
                        }}
                      >
                        <ItineraryBuilderView />
                      </ErrorBoundary>
                    )}
                    {tripsState.isPanelVisible('shortlist') && (
                      <ErrorBoundary
                        FallbackComponent={(props) => <TripPanelErrorFallback {...props} viewName="shortlist" />}
                        onError={(error, errorInfo) => {
                          console.error('Shortlist view error:', error, errorInfo);
                          sentry?.captureException(error);
                        }}
                        onReset={() => {
                          // Reset is handled by the error boundary's resetErrorBoundary
                        }}
                      >
                        <ShortlistView />
                      </ErrorBoundary>
                    )}
                    {tripsState.isPanelVisible('itinerary') && (
                      <ErrorBoundary
                        FallbackComponent={(props) => <TripPanelErrorFallback {...props} viewName="itinerary" />}
                        onError={(error, errorInfo) => {
                          console.error('Itinerary view error:', error, errorInfo);
                          sentry?.captureException(error);
                        }}
                        onReset={() => {
                          // Reset is handled by the error boundary's resetErrorBoundary
                        }}
                      >
                        <ItineraryView />
                      </ErrorBoundary>
                    )}
                    {tripsState.isPanelVisible('bookings') && (
                      <ErrorBoundary
                        FallbackComponent={(props) => <TripPanelErrorFallback {...props} viewName="bookings" />}
                        onError={(error, errorInfo) => {
                          console.error('Bookings view error:', error, errorInfo);
                          sentry?.captureException(error);
                        }}
                        onReset={() => {
                          // Reset is handled by the error boundary's resetErrorBoundary
                        }}
                      >
                        <BookingsView />
                      </ErrorBoundary>
                    )}
                    {/* {tripsState.isPanelVisible('tasks') && (
                      <ErrorBoundary
                        FallbackComponent={(props) => <TripPanelErrorFallback {...props} viewName="tasks" />}
                        onError={(error, errorInfo) => {
                          console.error('Tasks view error:', error, errorInfo);
                          sentry?.captureException(error);
                        }}
                        onReset={() => {
                          // Reset is handled by the error boundary's resetErrorBoundary
                        }}
                      >
                        <TasksView />
                      </ErrorBoundary>
                    )} */}
                  </div>
                </div>
              </div>
            </div>
            {tripsState.isMobile && (
              <MobileNavBar
                tabs={navTabs}
                animatingValue={tripsState.animatingPanel}
                animatingClassName={(v) => (v === 'shortlist' ? 'animate-nav-pulse-red' : 'animate-nav-pulse')}
              />
            )}
          </>
        ) : isLoadingTrips || isLoadingChats || isCreatingNewTrip ? (
          <>
            {/* Show loading state while data is being fetched */}
            <div className="flex-1 flex flex-col items-center justify-center overflow-scroll gap-4">
              <Loader loading={true} />
              <p className="text-muted-foreground">{isCreatingNewTrip ? 'Creating new trip...' : 'Loading trip...'}</p>
            </div>
          </>
        ) : (
          <>
            {/* Show error state if no trip is found after data loaded */}
            <div className="flex-1 flex flex-col items-center justify-center overflow-scroll gap-4">
              <div className="max-w-3xl mx-auto w-full px-4 py-[110px] md:py-[72px]">
                <div className="text-center">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Trip not found</h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    The trip you're looking for doesn't exist or has been deleted.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {currentTrip && (
        <>
          {/* Rename Trip Dialog */}
          <RenameTripDialog trip={currentTrip} />

          {/* Date Picker Dialog */}
          <EditTripDatesDialog trip={currentTrip} />

          {/* Share Itinerary Dialog */}
          <ShareItineraryDialog trip={currentTrip} />
        </>
      )}
    </AppLayout>
  );
}

function TripHeader({ displayDates = true, className }: { displayDates?: boolean; className?: string }) {
  const tripsState = useTripsState();

  const currentTrip = tripsState.current;
  if (!currentTrip) return null;

  return (
    <TripSettingsActions
      trip={currentTrip}
      trigger={
        <div
          className={cn(
            'w-full md:w-auto md:max-w-full z-40 flex flex-col items-start',
            'rounded-sm py-0.5 md:py-1 px-2 cursor-pointer hover:bg-background-tertiary',
            className,
          )}
        >
          <div className="text-xs md:text-sm font-semibold line-clamp-1 text-center md:text-left">
            {currentTrip.title}
          </div>
          {displayDates && (
            <div className="text-2xs text-muted-foreground text-center md:text-left">
              {currentTrip.formattedShortDateRange}
            </div>
          )}
        </div>
      }
    />
  );
}

function TripNavigationTabs({
  navTabs,
  className,
}: {
  navTabs: Array<{ value: TripPanelType; label: string; icon?: IconName }>;
  className?: string;
}): React.ReactNode {
  const tripsState = useTripsState();
  const uiState = useUIState();

  const indicatorTop = useMemo(() => {
    const gap = 8;
    const buttonHeight = 40;
    const index = navTabs.findIndex((t) => t.value === tripsState.activePanel);
    if (index !== -1) return (buttonHeight + gap) * index;
    return 0;
  }, [navTabs, tripsState.activePanel]);

  const sidebarLabelWrapper = 'absolute left-10 top-0 bottom-0 flex items-center overflow-hidden pointer-events-none';
  const sidebarLabelInner = cn(
    'whitespace-nowrap transition-opacity duration-200 ease-out',
    uiState.isSidebarOpen ? 'opacity-100' : 'opacity-0',
  );

  return (
    <>
      <div className={cn('flex-col items-start gap-2 w-full relative hidden md:flex')}>
        <div
          className="absolute bottom-0 transition-top duration-200 ease-in-out -right-2 w-[3px] h-10 bg-accent rounded-l-sm"
          style={{ top: indicatorTop }}
        />
        {navTabs.map((option) => (
          <Button
            key={option.value}
            variant={uiState.isSidebarOpen ? 'sidebar' : 'icon'}
            size={uiState.isSidebarOpen ? 'sidebar' : 'icon'}
            onClick={() => tripsState.switchToPanel(option.value as TripPanelType)}
            tooltip={uiState.isSidebarOpen ? undefined : option.label}
            tooltipPosition="right"
            className={cn(uiState.isSidebarOpen && 'w-full')}
            data-tour-highlight={`nav-${option.value}`}
          >
            <MaterialIcon
              icon={option.icon as IconName}
              filled={tripsState.activePanel === option.value || tripsState.animatingPanel === option.value}
              className={cn(
                tripsState.animatingPanel === option.value &&
                  (option.value === 'shortlist' ? 'animate-nav-pulse-red' : 'animate-nav-pulse'),
              )}
            />
            <span className={sidebarLabelWrapper}>
              <span className={sidebarLabelInner}>{option.label}</span>
            </span>
          </Button>
        ))}

        <SupportButton
          variant={uiState.isSidebarOpen ? 'sidebar' : 'icon'}
          size={uiState.isSidebarOpen ? 'sidebar' : 'icon'}
          className={cn(uiState.isSidebarOpen && 'w-full')}
          labelClassName={sidebarLabelWrapper}
          labelInnerClassName={sidebarLabelInner}
        />

        {tripsState.current && (
          <TripSettingsActions
            trip={tripsState.current}
            menuPosition="right"
            trigger={
              <Button
                variant={uiState.isSidebarOpen ? 'sidebar' : 'icon'}
                size={uiState.isSidebarOpen ? 'sidebar' : 'icon'}
                className={cn(uiState.isSidebarOpen && 'w-full')}
              >
                <MaterialIcon icon="more_horiz" />
                <span className={sidebarLabelWrapper}>
                  <span className={sidebarLabelInner}>Settings</span>
                </span>
              </Button>
            }
          />
        )}
      </div>

      {tripsState.isMobile && <TripHeader />}
    </>
  );
}
